<script>
import camelCase from 'lodash.camelcase'
import {debounce} from '@/helpers'
import moment from 'moment'
import axios from 'axios'

import {
  constructBasicMetrics,
  getArticles,
  constructTopList,
  deriveTopList,
} from '@/analytics/data-analyser'

import MultipleRankingLists from '@/components/MultipleRankingLists'

const props = {
  data: {
    type: Object,
  },
}

const defaultObjects = {
  keenAnalyses: null,
  basicMetrics: null,
  treemapItems: null,
  readership: null,
  funnelItems: null,
  funnelProcessed: null,
  barChartItems: null,
}

const notNullObjects = {
  topLists: {},
  metricItems: {},
}

const trueBooleanObjects = {
  metricItemsLoading: true,
}

export default {
  components: {
    MultipleRankingLists,
  },
  props: props,
  data () {
    return {
      cogData: undefined,
      ...defaultObjects,
      ...notNullObjects,
      ...trueBooleanObjects,
      dimensions: null,
      clientWidth: null,
      basicMetricProps: ['emailViews', 'webViews', 'engagedMinutes'],
      metricProps: [
        {name: 'Key Topics', value: 'topic', link: false, attr: 'custom'},
        {name: 'Readers', value: 'user', link: true, attr: 'custom_attributes.user_id'},
        {name: 'Articles', value: 'article', link: true, attr: 'custom_attributes.alertable_id'},
        {name: 'Analysts', value: 'analyst', link: true, attr: 'custom_attributes.primary_analyst_id'},
        {name: 'Channels', value: 'partner', link: true, attr: 'custom_attributes.partner_id'},
        {name: 'Companies', value: 'company', link: false, attr: 'custom_attributes.company_id'},
        {name: 'Geography', value: 'geography', link: false, attr: 'custom'},
        {name: 'Reader Countries', value: 'geo-country', link: false, attr: 'geo.country'},
        {name: 'Sectors', value: 'sector', link: false, attr: null},
      ],
      metricItemsType: 'treemap',
      articleMetrics: ['topic', 'geography'],
      metricActive: 'topic',
      debounceDimensions: null,
      minWidth: 600,
    }
  },
  computed: {
    sources () {
      return {sendgrid: ['email_open'], web: ['article_views']}
    },
    lineChartSources () {
      return {sendgrid: ['email_open'], web: ['article_views']}
    },
    timeChunks () {
      return (this.timeframe === 'ytd'
        || ['article', 'newsletter', 'alert'].includes(this.analyticsType)
        || (this.timeframe === 'this_13_weeks') && moment().subtract(180, 'day') < moment('2018-11-01', 'YYYY-MM-DD').toDate())
        ? ['current']
        : ['current', 'previous']
    },
    basicMetricsEmpty () {
      for (const prop of this.basicMetricsShow) {
        const stat = this.getStat(prop)
        if (stat > 0) {
          return false
        }
      }

      return true
    },
    basicMetricsReady () {
      return this.basicMetrics && this.basicMetrics.current
    },
    isDisabled () {
      return this.basicMetricsReady && this.basicMetricsEmpty
    },
    isIncomplete () {
      return this.basicMetricsReady && !this.basicMetricsEmpty && this.publishedPast2hours && this.enableWarning
    },
    publishedPast2hours () {
      const publishedAt = this.data.published_at || this.data.sentAt
      if (publishedAt !== null && publishedAt !== undefined) {
        const yst = moment().subtract(2, 'hour')
        const pub = moment(publishedAt)
        return yst < pub
      }

      return null
    },
  },
  watch: {
    data: function (newData, oldData) {
      this.resetPage(newData)
    },
  },
  mounted () {
    this.resetPage(this.data)
  },
  beforeDestroy () {
    this.resetAnalyticsObjects()
    this.removeListeners()
  },
  methods: {
    async loadData(type, timeframe, channel) {
      const response = await axios.get(
        `${process.env.VUE_APP_COGNIZANCE_URL}${type}/${channel ? `channel/${channel}` : 'network'}${timeframe ? `/${timeframe}` : ''}`
      )
      this.cogData = response.data
      return response
    },
    setDimensions () {
      if (document.getElementsByClassName('analytics-width-tracker')[0]) {
        const dim = document.getElementsByClassName('analytics-width-tracker')[0].getBoundingClientRect()

        this.dimensions = dim
        this.clientWidth = window.innerWidth
      }
    },
    resetPage (data) {
      if (data && (this.data.id !== null || this.data._id !== null)) {
        this.$nextTick(() => {
          this.resetAnalyticsObjects()
          this.setupListeners()
          this.setDimensions()
          // this.fetchStreamAnalyses()
        })
      }
    },
    resetAnalyticsObjects () {
      Object.keys(defaultObjects).map((key) => {
        this[key] = null
      })
      Object.keys(notNullObjects).map((key) => {
        this[key] = {}
      })
      Object.keys(trueBooleanObjects).map((key) => {
        this[key] = false
      })
    },
    setupListeners () {
      this.removeListeners()
      this.debounceDimensions = debounce(this.setDimensions, 20)

      window.addEventListener('resize', this.debounceDimensions)
    },
    removeListeners () {
      window.removeEventListener('resize', this.debounceDimensions)
      this.debounceDimensions = null
    },
    constructBasicMetrics (type = null) {
      this.basicMetrics = constructBasicMetrics(this.keenAnalyses, this.basicMetricProps, this.sources, this.timeChunks, type)
    },
    setMetricActive (metric) {
      this.metricActive = metric.toLowerCase()
    },
    getMetric (metric = null) {
      const result = this.metricProps.filter((obj) => {
        return obj.value === (metric != null ? metric.toLowerCase() : this.metricActive.toLowerCase())
      })

      return result ? result[0] : null
    },
    async fetchStreamAnalyses () {
      const type = this.analyticsType
      const locSource = {...this.sources}
      if (['channel', 'network'].includes(type)) {
        locSource.sendgrid = ['email_open', 'email_click']
      }

      // this.keenAnalyses = await this.$keen.fetchStreamAnalyses(type, locSource, this.timeframe, this.data.id === undefined ? this.data._id : this.data.id)
    },
    async calcArticles (channel_id = null) {
      const frameMap = {
        this_7_days: 7,
        this_30_days: 30,
        this_13_weeks: 90,
        ytd: moment().dayOfYear(),
      }

      const frameInt = frameMap[this.timeframe]

      const currentArt = await getArticles({
        published_start: frameInt,
        published_end: 0,
        channel_id,
      })

      const previousArt = this.timeframe === 'ytd'
        ? []
        : await getArticles({
          published_start: frameInt * 2,
          published_end: frameInt,
          channel_id,
        })

      // Non-reactive and reactive to only re-render once
      if (this.basicMetrics) {
        if (this.basicMetrics['previous'] && previousArt !== null) {
          this.basicMetrics['previous']['newArticles'] = previousArt.length
        }

        this.$set(this.basicMetrics['current'], 'newArticles', currentArt.length)

        this.calculateEngagementArticlesRatio()
      }
    },
    calculateEngagementArticlesRatio () {
      for (const chunk of this.timeChunks) {
        if (this.basicMetrics[chunk] !== undefined && this.basicMetrics[chunk]['newArticles'] > -1) {
          this.$set(
              this.basicMetrics[chunk],
              'engMinsArticlesRatio',
            this.basicMetrics[chunk]['newArticles'] > 0
              ? Math.round(this.basicMetrics[chunk]['engagedMinutes'] / this.basicMetrics[chunk]['newArticles'])
              : 0
          )
        }
      }
    },
    async constructTopList (indexBy, groupBy, attr, id, sources = null, context = null, resourceId = null, topRanks = 10, basicMetrics = null) {
      const streamPrefix = `${indexBy}-${groupBy}`

      // console.log('hereeeeeee',indexBy, groupBy, attr, (sources != null ? sources : this.sources), this.timeChunks, this.timeframe, id, context, resourceId, topRanks, basicMetrics)
      const topList = await constructTopList(indexBy, groupBy, attr, (sources != null ? sources : this.sources), this.timeChunks, this.timeframe, id, context, resourceId, topRanks, basicMetrics)

      // this.$set(this.topLists, camelCase(streamPrefix), topList)

      return topList
    },
    async constructTreemap () {
      this.treemapItems = await this.constructTopList(this.analyticsType, 'account', 'custom_attributes.account_id', this.data.id === undefined ? this.data._id : this.data.id, this.sources)
    },
    async constructMetricItemsOthers () {
      for (const metric of this.metricProps) {
        if (metric.attr != null && metric.value !== this.metricActive) {
          await this.constructMetricItems(false, metric.value)
        }
      }
    },


    async constructMetricItems (activeRender = true, metricPar = null) {
      const metric = metricPar != null ? metricPar : this.metricActive
      // console.log(this.metricItems, metric)
      if (!this.metricItems[metric]) {
        this.metricItemsLoading = true

        // Derive top list values from 'article' resource
        if (this.articleMetrics.includes(metric)) {
          if (!this.metricItems['article']) {
            this.metricItems['article'] = await this.constructTopList(this.analyticsType, 'article', this.getMetric('article').attr, this.data.id === undefined ? this.data._id : this.data.id, this.sources)
          }
          this.metricItems[metric] = deriveTopList(this.metricItems['article'], metric, 20, undefined, this.timeframe)

        // Fetch & construct top list values for resource
        } else {
          if (!this.metricItems[metric]) {
            this.metricItems[metric] = null
            // console.log('here/', this.getMetric(metric).attr)
            this.metricItems[metric] = await constructTopList(this.analyticsType, metric, this.getMetric(metric).attr, this.sources, this.timeChunks, this.timeframe, this.data.id === undefined ? this.data._id : this.data.id)
          }
        }

        if (activeRender) {
          this.$forceUpdate()
        }
      }
      this.metricItemsLoading = false
    },

    async constructKeyTopics (sources = null) {
      const articles = await this.constructTopList(this.analyticsType, 'article', 'custom_attributes.alertable_id', this.data.id === undefined ? this.data._id : this.data.id, sources ? sources : this.sources, 'infinity-rank')

      // this.$set(this.topLists, `${this.analyticsType}Topic`, deriveTopList(articles, 'topic', 10, 'infinity-rank', this.timeframe))
      // this.$set(this.topLists, `${this.analyticsType}Article`, this.topLists[`${this.analyticsType}Article`])
    },
    sourcesPlus (params) {
      const result = {web: this.sources.web.slice(), email: (this.sources.email || []).slice()}

      for (const key in params) {
        if (params[key] !== undefined) {
          result[key] = key in result ? [...new Set(result[key].concat(params[key]))] : result[key] = params[key]
        }
      }

      return result
    },
    getStat (prop) {
      return this.basicMetrics !== null && this.basicMetrics.current !== null && prop in this.basicMetrics.current ? this.basicMetrics.current[prop] : null
    },
  },
}
</script>

<style lang="scss">
.analytics {
  h1, h2, h3 {
    cursor: default !important;
    // color: #1f2638;
    color: black;
  }

  .alert {
    border-radius: 2px;

    color: #1f2638;
    position: relative;
    padding: 6px 30px 6px 18px;
    &.alert-warning {
      background: #fffdf5;
      border: 1px solid #ffc107;
    }
    &.alert-success {
      background: rgba(0, 190, 158, 0.1);
      border-color: #00BE9E;
    }
    .close-icon {
      position: absolute;
      right: 8px;
      top: 8px;
      margin: 0;
      cursor: pointer;
    }
  }

  svg > g {
    .selector {
      fill: #f3d9d2;
      fill-opacity: 0.2;
    }
  }
  .popup {
    position: absolute;
    top: 0;
    background: white;
    box-shadow: 0 7px 32px rgba(0, 0, 0, 0.12);
    padding: 12px 12px 6px 12px;
    color: #1f2638;
    width: 180px;
    font-size: 14px;
    span, div {
      cursor: default !important;
    }
    .popup-title {
      line-height: 1;
    }
    .popup-tip {
      width: 16px;
      height: 16px;
      position: absolute;
      margin-left: -7px;
      bottom: -7px;
      left: 50%;
      transform: rotate(45deg);
      background: white;
    }
    .categories {
      margin-top: 28px;
      .category {
        margin: 8px 0;
        padding: 5px 0 5px 8px;
        line-height: 16px;
        &:first-child {
          border-left: 2px solid #0e094d;
        }
        &:nth-child(2) {
          border-left: 2px solid #0364f6;
        }
        &:nth-child(3), &.network {
          border-left: 2px solid #62ddc7;
        }
        .cat-value {
          display: inline-block;
          margin-left: 6px;
        }
      }
      &.four-children .category {
        &:nth-child(3) {
          border-left: 2px solid #58a0e0;
        }
        &:nth-child(4) {
          border-left: 2px solid #62ddc7;
        }
      }
    }
  }
}
</style>
